import { create } from 'zustand';

import { toast } from '@/utils/toast';

export const HANDLED_BACKGROUND_TASK_ERROR = 'HandledBackgroundTaskError';

export type BgTaskType = 'maintenance' | 'equipment' | 'itinerary';

export type BgTask = {
  id: string;
  type: BgTaskType;
  label: string;
  status: 'pending' | 'success' | 'error';
  result?: unknown;
  error?: string;
  navigateTo?: string;
  controller?: AbortController;
};

type RunBgTaskInput = {
  type: BgTaskType;
  label: string;
  navigateTo?: string;
  fn: (signal: AbortSignal) => Promise<unknown>;
};

type BackgroundTasksStore = {
  tasks: Record<string, BgTask>;
  runTask: (input: RunBgTaskInput) => Promise<void>;
  acknowledge: (id: string) => void;
  abortAll: () => void;
};

const hasPending = (tasks: Record<string, BgTask>) =>
  Object.values(tasks).some((t) => t.status === 'pending');

export const useBackgroundTasksStore = create<BackgroundTasksStore>((set, get) => ({
  tasks: {},
  runTask: async ({ type, label, navigateTo, fn }) => {
    if (hasPending(get().tasks)) {
      toast.info('Please wait for the current extract to finish');
      return;
    }
    const id = crypto.randomUUID();
    const controller = new AbortController();
    set((s) => ({
      tasks: {
        ...s.tasks,
        [id]: { id, type, label, status: 'pending', navigateTo, controller },
      },
    }));
    try {
      const result = await fn(controller.signal);
      set((s) => {
        if (!s.tasks[id]) return s;
        return {
          tasks: {
            ...s.tasks,
            [id]: { ...s.tasks[id], status: 'success', result, controller: undefined },
          },
        };
      });
    } catch (e) {
      if (controller.signal.aborted) {
        set((s) => {
          const next = { ...s.tasks };
          delete next[id];
          return { tasks: next };
        });
        return;
      }
      if (e instanceof Error && e.name === HANDLED_BACKGROUND_TASK_ERROR) {
        set((s) => {
          const next = { ...s.tasks };
          delete next[id];
          return { tasks: next };
        });
        return;
      }
      const error = e instanceof Error ? e.message : String(e);
      set((s) => {
        if (!s.tasks[id]) return s;
        return {
          tasks: {
            ...s.tasks,
            [id]: { ...s.tasks[id], status: 'error', error, controller: undefined },
          },
        };
      });
    }
  },
  acknowledge: (id) =>
    set((s) => {
      const next = { ...s.tasks };
      delete next[id];
      return { tasks: next };
    }),
  abortAll: () => {
    const tasks = get().tasks;
    Object.values(tasks).forEach((t) => t.controller?.abort());
    set({ tasks: {} });
  },
}));
